import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ProductionDeclarationMaterialProduct } from '@/types/core/purchase-order/production-declaration-material-product';

const MATERIAL_PRODUCTS_SKELETON_ROW_KEYS = [
  'material-products-skeleton-1',
  'material-products-skeleton-2',
  'material-products-skeleton-3',
  'material-products-skeleton-4',
] as const;

const MATERIAL_PRODUCTS_SKELETON_CELL_KEYS = [
  'miliboo-sku',
  'factory-pn',
  'description',
  'relevant-commodity',
  'hs-code',
] as const;

type ProductionDeclarationMaterialProductsListProps = {
  products: ProductionDeclarationMaterialProduct[];
  isLoading?: boolean;
};

export function ProductionDeclarationMaterialProductsList({
  products,
  isLoading = false,
}: ProductionDeclarationMaterialProductsListProps) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });

  return (
    <section className='overflow-hidden rounded-[1.75rem] border border-declaration-border bg-white px-7 py-8 shadow-[0_10px_24px_rgba(17,24,39,0.06)] sm:px-10'>
      <div className='max-w-full overflow-x-auto'>
        {isLoading ? (
          <Table className='min-w-[68rem] table-fixed'>
            <TableHeader>
              <TableRow className='border-declaration-muted hover:bg-transparent'>
                <TableHead className='w-[10rem] px-3 pb-3 font-medium text-base text-declaration-muted-foreground'>
                  {t('materialProducts.columns.milibooSku')}
                </TableHead>
                <TableHead className='w-[12rem] px-3 pb-3 font-medium text-base text-declaration-muted-foreground'>
                  {t('materialProducts.columns.factoryPn')}
                </TableHead>
                <TableHead className='w-[22rem] px-3 pb-3 font-medium text-base text-declaration-muted-foreground'>
                  {t('materialProducts.columns.description')}
                </TableHead>
                <TableHead className='w-[14rem] px-3 pb-3 font-medium text-base text-declaration-muted-foreground'>
                  {t('materialProducts.columns.relevantCommodity')}
                </TableHead>
                <TableHead className='w-[10rem] px-3 pb-3 font-medium text-base text-declaration-muted-foreground'>
                  {t('materialProducts.columns.hsCode')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {MATERIAL_PRODUCTS_SKELETON_ROW_KEYS.map((rowKey) => (
                <TableRow
                  className='border-declaration-border-light hover:bg-transparent'
                  key={rowKey}
                >
                  {MATERIAL_PRODUCTS_SKELETON_CELL_KEYS.map((cellKey) => (
                    <TableCell className='px-3 py-5' key={cellKey}>
                      <div className='h-8 rounded-full bg-declaration-skeleton' />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : products.length === 0 ? (
          <div className='py-10 text-center text-declaration-muted-foreground'>
            {t('materialProducts.empty')}
          </div>
        ) : (
          <Table className='min-w-[68rem] table-fixed'>
            <TableHeader>
              <TableRow className='border-declaration-muted hover:bg-transparent'>
                <TableHead className='w-[10rem] px-3 pb-3 font-medium text-base text-declaration-muted-foreground'>
                  {t('materialProducts.columns.milibooSku')}
                </TableHead>
                <TableHead className='w-[12rem] px-3 pb-3 font-medium text-base text-declaration-muted-foreground'>
                  {t('materialProducts.columns.factoryPn')}
                </TableHead>
                <TableHead className='w-[22rem] px-3 pb-3 font-medium text-base text-declaration-muted-foreground'>
                  {t('materialProducts.columns.description')}
                </TableHead>
                <TableHead className='w-[14rem] px-3 pb-3 font-medium text-base text-declaration-muted-foreground'>
                  {t('materialProducts.columns.relevantCommodity')}
                </TableHead>
                <TableHead className='w-[10rem] px-3 pb-3 font-medium text-base text-declaration-muted-foreground'>
                  {t('materialProducts.columns.hsCode')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => (
                <TableRow
                  className='border-declaration-border-light hover:bg-transparent'
                  key={product.purchaseOrderLineId}
                >
                  <TableCell className='w-[10rem] px-3 py-5 font-semibold text-[1.05rem] text-declaration-foreground leading-7'>
                    {product.milibooSku ?? '—'}
                  </TableCell>
                  <TableCell className='w-[12rem] px-3 py-5 text-[1.05rem] text-declaration-foreground-muted leading-7'>
                    {product.factoryPn ?? '—'}
                  </TableCell>
                  <TableCell
                    className='w-[22rem] truncate px-3 py-5 text-[1.05rem] text-declaration-foreground-muted leading-7'
                    title={product.description ?? undefined}
                  >
                    {product.description ?? '—'}
                  </TableCell>
                  <TableCell className='w-[14rem] px-3 py-5 text-[1.05rem] text-declaration-foreground-muted leading-7'>
                    {product.relevantCommodity}
                  </TableCell>
                  <TableCell className='w-[10rem] px-3 py-5 text-[1.05rem] text-declaration-foreground-muted leading-7'>
                    {product.hsCode ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  );
}
